/**
 * effective-tier (client mirror of apps/web/api/_lib/auth/effective-tier.ts).
 *
 * ADR-0021: shared gate predicate. Pure functions, no Supabase imports —
 * safe to ship in the browser bundle. Server-side mirror MUST stay byte-
 * compatible (admin → 'max', etc.) so client gate UX matches the server's
 * 401/403 decision.
 *
 * Why two copies: the API directory (`apps/web/api/`) is Vercel functions
 * land — Vite doesn't bundle it. Rather than introduce a third package
 * just for one tiny helper, we keep a parallel client copy. Tests on both
 * sides assert the same behavior table.
 */

export type Tier = 'anonymous' | 'free' | 'pro' | 'max';
export type Role = 'user' | 'admin';

export interface ProfileLike {
  tier?: string | null;
  role?: string | null;
}

const TIER_ORDER: ReadonlyArray<Tier> = ['anonymous', 'free', 'pro', 'max'];

/**
 * Aligns with jubit-ai-universe's `tier_limits` table:
 * 'anonymous' | 'free' | 'pro' | 'max'. Legacy 'plus' is folded into 'pro'.
 */
export function coerceTier(value: string | null | undefined): Tier {
  if (value === 'free' || value === 'pro' || value === 'max' || value === 'anonymous') {
    return value;
  }
  if (value === 'plus') return 'pro';
  return 'free';
}

export function coerceRole(value: string | null | undefined): Role {
  return value === 'admin' ? 'admin' : 'user';
}

export function getEffectiveTier(profile: ProfileLike | null | undefined): Tier {
  if (!profile) return 'anonymous';
  if (coerceRole(profile.role) === 'admin') return 'max';
  return coerceTier(profile.tier);
}

export function meetsTier(user: Tier, required: Tier): boolean {
  return TIER_ORDER.indexOf(user) >= TIER_ORDER.indexOf(required);
}

export function canAccessStudio(profile: ProfileLike | null | undefined): boolean {
  return meetsTier(getEffectiveTier(profile), 'max');
}

/**
 * Display label for tier badges (capitalized — RED-style). Anonymous
 * collapses to 'Free' since badge surfaces show signed-in copy by
 * convention (use `tierPillLabel` for header pills that need 'GUEST').
 *
 * Centralizes the coercion that used to live inline in
 * ProfileScreen — see PR #295. Adding a new tier label means
 * editing one file.
 */
export function tierBadgeLabel(tier: Tier): 'Free' | 'Pro' | 'Max' {
  if (tier === 'pro') return 'Pro';
  if (tier === 'max') return 'Max';
  // 'anonymous' + 'free' + any unknown → 'Free' (signed-in display
  // convention; anonymous users still get the tier upsell below).
  return 'Free';
}

/**
 * Display label for header tier-pills (ALL-CAPS — game-UI style).
 * Anonymous renders as 'GUEST' so the pill self-discloses the
 * signed-out state instead of misrepresenting tier.
 *
 * Centralizes the coercion that used to live inline in
 * TopHeader — see PR #295.
 */
export function tierPillLabel(tier: Tier): 'GUEST' | 'FREE' | 'PRO' | 'MAX' {
  if (tier === 'anonymous') return 'GUEST';
  if (tier === 'pro') return 'PRO';
  if (tier === 'max') return 'MAX';
  return 'FREE';
}

/**
 * Tailwind-compatible class string for header / chatlab tier pills.
 * Centralizes the visual mapping so accent updates are one edit.
 *
 * - max → gold pill (premium)
 * - pro → violet pill (mid)
 * - free / anonymous → neutral
 *
 * PR #296 — was inline in TopHeader + ChatlabScreen (third copy after
 * the label coercion in PR #295). Same pattern, same fix.
 */
export function tierPillClass(tier: Tier): string {
  if (tier === 'max') return 'jub-pill jub-pill-gold';
  if (tier === 'pro') return 'jub-pill jub-pill-violet';
  return 'jub-pill';
}
